import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Alert,
  Spin,
  Row,
  Col,
} from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { fetchHomeSections, updateHomeSections } from '@/api/homeContent';

const SECTION_META = {
  hot: {
    name: '新品 / 热门板块',
    desc: '首页商品列表第一个板块，展示当季新品或热销茶叶',
    titlePlaceholder: '如：本季新茶',
    subtitlePlaceholder: '如：刚到店的鲜爽滋味（可留空）',
  },
  recommend: {
    name: '推荐板块',
    desc: '首页第二个商品板块，展示店铺主推商品',
    titlePlaceholder: '如：精选推荐',
    subtitlePlaceholder: '板块副标题（可留空）',
  },
  discount: {
    name: '特惠板块',
    desc: '限时特惠活动板块的标题',
    titlePlaceholder: '如：限时特惠',
    subtitlePlaceholder: '板块副标题（可留空）',
  },
  category: {
    name: '分类板块',
    desc: '首页商品分类导航区的标题',
    titlePlaceholder: '如：商品分类',
    subtitlePlaceholder: '如：按茶类挑选心仪滋味（可留空）',
  },
};

const SECTION_KEYS = ['hot', 'recommend', 'discount', 'category'];

const HomeSectionsPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSections = async () => {
    setLoading(true);
    try {
      const res = await fetchHomeSections();
      if (res.code === 200 || res.success) {
        const data = res.data || {};
        const values = {};
        SECTION_KEYS.forEach((key) => {
          values[`${key}.title`] = data[key]?.title || '';
          values[`${key}.subtitle`] = data[key]?.subtitle || '';
        });
        form.setFieldsValue(values);
      } else {
        message.error(res.message || '加载失败');
      }
    } catch (e) {
      message.error('加载首页板块配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {};
      SECTION_KEYS.forEach((key) => {
        payload[key] = {
          title: values[`${key}.title`] || '',
          subtitle: values[`${key}.subtitle`] || '',
        };
      });
      const res = await updateHomeSections(payload);
      if (res.code === 200 || res.success) {
        message.success('首页板块文案已更新，小程序端实时生效');
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      if (!e?.errorFields) message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">首页板块</h1>
          <p className="workspace-description">配置小程序首页各板块的标题与副标题文案</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadSections}>刷新</Button>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="修改保存后小程序首页实时生效；板块是否展示取决于该板块下是否有商品"
      />

      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          {SECTION_KEYS.map((key) => {
            const meta = SECTION_META[key];
            return (
              <Card
                key={key}
                size="small"
                title={meta.name}
                style={{ marginBottom: 16 }}
              >
                <div style={{ color: '#66736D', fontSize: 13, marginBottom: 12 }}>
                  {meta.desc}
                </div>
                <Row gutter={16}>
                  <Col xs={24} md={10}>
                    <Form.Item
                      name={`${key}.title`}
                      label="板块标题"
                      rules={[
                        { required: true, message: '请输入板块标题' },
                        { max: 30, message: '标题不能超过 30 字' },
                      ]}
                    >
                      <Input placeholder={meta.titlePlaceholder} allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={14}>
                    <Form.Item
                      name={`${key}.subtitle`}
                      label="板块副标题"
                      rules={[{ max: 60, message: '副标题不能超过 60 字' }]}
                    >
                      <Input placeholder={meta.subtitlePlaceholder} allowClear />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            );
          })}

          <Button
            type="primary"
            icon={<EditOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            保存全部
          </Button>
        </Form>
      </Spin>
    </div>
  );
};

export default HomeSectionsPage;
